import React from 'react';
import { useActiveWeb3React } from '../../../hooks';
import { LikeServices, ProfileServices } from './Services';
import { ethers } from 'ethers'
import { useGetUserNFTLikes } from './useGetUserNFTLikes';
import { useNFTLikeCount } from './useNFTLikeCount';
import { useWalletModalToggle } from '../../../state/application/hooks';


const useNFTLike = (contractAddress: string, tokenID: number) => {
    const { account, library } = useActiveWeb3React()
    const { count: likeCount, addToLiked: addToLikedCount, removeFromLiked: removeFromLikedCount } = useNFTLikeCount(contractAddress, tokenID);
    const [isLiked, setIsLiked] = React.useState(false);
    const toggleWalletModal = useWalletModalToggle()
    const { likes, isLoading, addToLiked, removeFromLiked } = useGetUserNFTLikes(account as any);

    React.useEffect(() => {
        setIsLiked(likes.findIndex(like => like.tokenID.toString() == tokenID?.toString() && like.collectionAddress?.toLowerCase() == contractAddress.toLowerCase()) > -1);
    }, [account, likes, contractAddress, tokenID]);

    const getLikeSignature = React.useCallback(async () => {
        let message = `I want to like Collection=${contractAddress.toLowerCase()} TokenID=${tokenID} Nonce=`;
        const nonce: string = await ProfileServices.GetNonceForUser(account as string)
        if (nonce == undefined || nonce == null) return null;
        message += nonce

        const signer = await library?.getSigner()
        const signature = await signer?.signMessage(message)
        if (signature && account) {
            const signerAddress = await ethers.utils.verifyMessage(message, signature)
            if (signerAddress == account) {
                return signature
            }
        }
        return null;
    }, [account, contractAddress, tokenID]);

    const getUnLikeSignature = React.useCallback(async () => {
        let message = `I want to unlike Collection=${contractAddress.toLowerCase()} TokenID=${tokenID} Nonce=`;
        const nonce: string = await ProfileServices.GetNonceForUser(account as string)
        if (!nonce) return null;
        message += nonce

        const signer = await library?.getSigner()
        const signature = await signer?.signMessage(message)
        if (signature && account) {
            const signerAddress = await ethers.utils.verifyMessage(message, signature)
            if (signerAddress == account) {
                return signature
            }
        }
        return null;
    }, [account, contractAddress, tokenID]);

    const likeNFTAction = React.useCallback(async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!account) {
            toggleWalletModal();
            return;
        }
        let signature = await getLikeSignature();

        if (!signature) {
            return;
        }

        let result = await LikeServices.LikeNFT({
            userAddress: account.toLowerCase(),
            collectionAddress: contractAddress.toLowerCase(),
            tokenID: tokenID.toString(),
            signature
        })

        if (result) {
            addToLiked(contractAddress, tokenID);
            setIsLiked(true);
            addToLikedCount(contractAddress, tokenID);
        }
    }, [account, contractAddress, tokenID, getLikeSignature, addToLiked, addToLikedCount, setIsLiked]);

    const unlikeNFTAction = React.useCallback(async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!account) {
            toggleWalletModal();
            return;
        }
        let signature = await getUnLikeSignature();

        if (!signature) {
            return;
        }

        let result = await LikeServices.UnLikeNFT({
            userAddress: account.toLowerCase(),
            collectionAddress: contractAddress.toLowerCase(),
            tokenID: tokenID.toString(),
            signature
        })

        if (result) {
            removeFromLiked(contractAddress, tokenID);
            setIsLiked(false);
            removeFromLikedCount(contractAddress, tokenID);
        }
    }, [account, contractAddress, tokenID, getUnLikeSignature, removeFromLiked, removeFromLikedCount, setIsLiked]);

    const action = isLoading ? undefined : (isLiked ? unlikeNFTAction : likeNFTAction);
    const actionTitle = !account ? 'Connect Wallet to Like' : (isLoading ? 'Loading...' : (isLiked ? 'Unlike' : 'Like'));
    return {
        action,
        actionTitle,
        isLiked,
        likeCount
    }
}

export default useNFTLike;